/// TabBar: Horizontal tab bar showing open editor tabs.
/// Each tab shows type icon, title, dirty indicator, and close button.
/// Supports click to switch, X to close (with dirty confirmation), and + to add new tab.
///
/// Panel-aware: receives a panelId so it displays only the tabs assigned
/// to its panel and routes new-tab creation to the correct panel.
///
/// When tabs overflow the available width, VS Code-style navigation arrows
/// appear at the edges to allow horizontal scrolling without a native scrollbar.

import { useCallback, useRef, useState, useEffect } from "react";
import { X, FileCode, GitBranch, Plus, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { useEditorStore, type PanelId } from "@/stores/editorStore";
import type { EditorTab, EditorType } from "@/types/editor";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/common/DropdownMenu";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** How many pixels each arrow click scrolls the tab list. */
const SCROLL_STEP = 150;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TabBarProps {
  /** Panel identity — determines which tabs to show and where new tabs go. */
  panelId: PanelId;
}

// ---------------------------------------------------------------------------
// Tab icon by editor type
// ---------------------------------------------------------------------------

function TabIcon({ editorType }: { editorType: EditorType }) {
  if (editorType === "bpmn") {
    return <GitBranch className="h-3.5 w-3.5 text-blue-400 shrink-0" />;
  }
  return <FileCode className="h-3.5 w-3.5 text-green-400 shrink-0" />;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

let tabCounter = 0;

export function TabBar({ panelId }: TabBarProps) {
  const allTabs = useEditorStore((s) => s.tabs);
  const panelTabIds = useEditorStore((s) => s.panelTabs[panelId]);
  const activeTabId = useEditorStore((s) => s.activeTabId);
  const setActiveTab = useEditorStore((s) => s.setActiveTab);
  const removeTab = useEditorStore((s) => s.removeTab);
  const addTab = useEditorStore((s) => s.addTab);

  // Show only tabs assigned to this panel
  const tabs = allTabs.filter((t) => panelTabIds.includes(t.id));

  // --- Scroll overflow state ---
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  /** Recalculate whether arrow buttons should be visible. */
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const tolerance = 1; // sub-pixel rounding tolerance
    setCanScrollLeft(el.scrollLeft > tolerance);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance);
  }, []);

  // Listen to scroll events on the tab container
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    return () => el.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  // Observe resize changes (tab added/removed, container resized)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => updateScrollState());
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollState]);

  // Re-check when tab count changes (content changes without resize)
  useEffect(() => {
    updateScrollState();
  }, [tabs.length, updateScrollState]);

  const scrollLeft = useCallback(() => {
    scrollRef.current?.scrollBy({ left: -SCROLL_STEP, behavior: "smooth" });
  }, []);

  const scrollRight = useCallback(() => {
    scrollRef.current?.scrollBy({ left: SCROLL_STEP, behavior: "smooth" });
  }, []);

  const handleClose = useCallback(
    (e: React.MouseEvent, tab: EditorTab) => {
      e.stopPropagation();
      if (tab.dirty) {
        // Simple confirm for now; can be replaced with a dialog later
        if (!window.confirm(`"${tab.title}" has unsaved changes. Close anyway?`)) {
          return;
        }
      }
      removeTab(tab.id);
    },
    [removeTab],
  );

  const handleAddTab = useCallback(
    (editorType: EditorType = "nodeflow") => {
      tabCounter += 1;
      const label = editorType === "bpmn" ? "BPMN" : "Node";
      addTab(
        {
          id: `tab-${Date.now()}-${tabCounter}`,
          title: `Untitled ${label} ${tabCounter}`,
          editorType,
          content: "",
          dirty: false,
          canUndo: false,
          canRedo: false,
        },
        panelId,
      );
      // Scroll to show the newly created tab (rendered at the end)
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (el) el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
      });
    },
    [addTab, panelId],
  );

  const showArrows = canScrollLeft || canScrollRight;

  // --- Split "new tab" button: [+][▾] ---
  const newTabButton = (
    <div className="flex items-center h-8 shrink-0">
      {/* Main button — creates default tab (Node) */}
      <button
        type="button"
        className="flex items-center justify-center h-8 w-7 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors"
        onClick={() => handleAddTab()}
        title="New Tab (Node)"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
      {/* Dropdown chevron — pick editor type */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-center h-8 w-5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors"
            title="Choose diagram type"
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={4} className="max-w-[280px]">
          <DropdownMenuItem onSelect={() => handleAddTab("bpmn")}>
            <GitBranch className="h-4 w-4 text-blue-400" />
            New BPMN Diagram
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => handleAddTab("nodeflow")}>
            <FileCode className="h-4 w-4 text-green-400" />
            New Node Diagram
          </DropdownMenuItem>
          {tabs.length > 0 && <DropdownMenuSeparator />}
          {tabs.map((tab) => {
            const isActive = tab.id === activeTabId;
            return (
              <DropdownMenuItem
                key={tab.id}
                onSelect={() => setActiveTab(tab.id)}
                className={isActive ? "bg-zinc-700/50" : undefined}
              >
                <TabIcon editorType={tab.editorType} />
                <span className="min-w-0 flex-1 truncate">{tab.title}</span>
                {tab.dirty && (
                  <span
                    className="h-2 w-2 rounded-full bg-zinc-400 shrink-0"
                    title="Unsaved changes"
                  />
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <div data-testid="tab-bar" className="flex h-9 items-end bg-zinc-900 border-b border-zinc-700 select-none">
      {/* Left scroll arrow */}
      {showArrows && (
        <button
          type="button"
          className={`flex items-center justify-center h-8 w-6 shrink-0 transition-colors ${
            canScrollLeft
              ? "text-zinc-300 hover:bg-zinc-700 cursor-pointer"
              : "text-zinc-600 cursor-default"
          }`}
          onClick={scrollLeft}
          disabled={!canScrollLeft}
          title="Scroll tabs left"
          aria-label="Scroll tabs left"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Scrollable tab container - native scrollbar hidden */}
      <div
        ref={scrollRef}
        className="flex flex-1 items-end overflow-x-auto scrollbar-hidden min-w-0"
        style={{ scrollbarWidth: "none" } as React.CSSProperties}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <button
              key={tab.id}
              type="button"
              className={`group relative flex items-center gap-1.5 px-3 h-8 text-xs border-r border-zinc-700 shrink-0 transition-colors ${
                isActive
                  ? "bg-zinc-800 text-zinc-100 border-t-2 border-t-blue-500"
                  : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-300 border-t-2 border-t-transparent"
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <TabIcon editorType={tab.editorType} />
              <span className="max-w-[120px] truncate">{tab.title}</span>
              {tab.dirty && (
                <span
                  className="h-2 w-2 rounded-full bg-zinc-400 shrink-0"
                  title="Unsaved changes"
                />
              )}
              <span
                role="button"
                tabIndex={-1}
                className="ml-1 rounded p-0.5 opacity-0 group-hover:opacity-100 hover:bg-zinc-600 transition-opacity shrink-0"
                onClick={(e) => handleClose(e, tab)}
                title="Close"
              >
                <X className="h-3 w-3" />
              </span>
            </button>
          );
        })}

        {/* When NOT overflowing: new-tab button follows last tab */}
        {!showArrows && newTabButton}
      </div>

      {/* When overflowing: new-tab button sits outside scroll area */}
      {showArrows && newTabButton}

      {/* Right scroll arrow */}
      {showArrows && (
        <button
          type="button"
          className={`flex items-center justify-center h-8 w-6 shrink-0 transition-colors ${
            canScrollRight
              ? "text-zinc-300 hover:bg-zinc-700 cursor-pointer"
              : "text-zinc-600 cursor-default"
          }`}
          onClick={scrollRight}
          disabled={!canScrollRight}
          title="Scroll tabs right"
          aria-label="Scroll tabs right"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
